import type { DocumentSide } from '@/lib/checkin-types';
import type { HistoryEvent } from '@/lib/history-types';
import type { ScanResult } from '@/lib/scan-types';

/**
 * The browser's only way out of the app (see CLAUDE.md — components never call
 * `fetch` directly).
 *
 * Every route here is one of ours, never the CMS: the session token is httpOnly
 * and server-side, so the browser could not authenticate against Payload even
 * if it tried. That is the point — a token that authorises writes to guest
 * passport data should not be reachable from page JavaScript.
 */

class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const request = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
  const response = await fetch(path, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...init.headers },
  });

  const body = (await response.json().catch(() => null)) as (T & { message?: string }) | null;

  if (!response.ok) {
    throw new ApiError(body?.message ?? `Request failed (${response.status})`, response.status);
  }

  return body as T;
};

export const apiClient = {
  login: (email: string, password: string) =>
    request<{ ok: true }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),

  /** Stores the language choice. The caller refreshes so the server re-renders. */
  setLocale: (locale: string) =>
    request<{ ok: true }>('/api/locale', {
      method: 'POST',
      body: JSON.stringify({ locale }),
    }),

  /**
   * Sends one photographed travel document to be read, and to be kept.
   *
   * Deliberately not routed through `request`: that helper sets a JSON content
   * type, and a multipart body needs the browser to set its own header with the
   * boundary token. The photo goes up as raw bytes rather than base64 because a
   * phone camera file is several megabytes and base64 would add a third to it
   * over a hotel's wifi.
   *
   * `where` says which person on which reservation the photograph belongs to.
   * It is optional so the route stays usable without a reservation behind it —
   * that scan reads the document and stores nothing, which is the right
   * behaviour for a test page and the wrong one to make the caller opt out of.
   */
  scanDocument: async (
    image: Blob,
    where?: {
      token: string;
      position: number;
      side?: DocumentSide;
      /** What the guest said they are holding — decides which side carries a zone. */
      documentType?: string;
    },
    signal?: AbortSignal
  ): Promise<ScanResult> => {
    const body = new FormData();
    body.append('image', image, 'document');
    if (where) {
      body.append('token', where.token);
      body.append('position', String(where.position));
      if (where.side) body.append('side', where.side);
      if (where.documentType) body.append('documentType', where.documentType);
    }

    const response = await fetch('/api/scan', { method: 'POST', body, signal });
    const parsed = (await response.json().catch(() => null)) as
      | (ScanResult & { error?: string })
      | null;

    if (!response.ok) {
      throw new ApiError(parsed?.error ?? `Scan failed (${response.status})`, response.status);
    }
    return parsed as ScanResult;
  },

  /**
   * Removes the photographs filed against one person, or one side of them.
   *
   * Called when the guest changes what kind of document they are presenting:
   * the sides a passport needs are not the sides an identity card needs, and
   * the leftovers would be shown to reception beside a document that is no
   * longer theirs. A retake does not come through here — the CMS replaces a
   * side as it writes it.
   */
  removeScans: async (token: string, position: number, side?: DocumentSide): Promise<void> => {
    const query = new URLSearchParams({ token, position: String(position) });
    if (side) query.set('side', side);

    const response = await fetch(`/api/scan?${query}`, { method: 'DELETE' });
    if (!response.ok) {
      throw new ApiError(`Could not remove the photo (${response.status})`, response.status);
    }
  },

  /** The audit trail for one card, newest first. Loaded on open, not up front. */
  /**
   * One guest's registration-of-stay certificate, as a file the browser can
   * save (#52). Fetched rather than linked so a refusal — a field the
   * certificate needs, a hotel with no address set — comes back as a sentence
   * instead of a tab full of JSON.
   */
  stayCertificate: async (
    reservationId: string,
    position: number
  ): Promise<{ blob: Blob; filename: string }> => {
    const response = await fetch(
      `/api/reservations/${encodeURIComponent(reservationId)}/stay-certificate/${position}`
    );

    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as { message?: string } | null;
      throw new ApiError(body?.message ?? `Request failed (${response.status})`, response.status);
    }

    const filename = /filename="([^"]+)"/.exec(response.headers.get('Content-Disposition') ?? '');
    return { blob: await response.blob(), filename: filename?.[1] ?? 'certificate.pdf' };
  },

  history: (reservationId: string) =>
    request<{ events: HistoryEvent[] }>(
      `/api/reservations/${encodeURIComponent(reservationId)}/history`
    ).then((body) => body.events),
};

export { ApiError };
